import React, { useCallback, useEffect, useState } from "react";

import { HomeOutlined, WarningOutlined } from "@ant-design/icons";
import { UseMutateFunction } from "@tanstack/react-query";
import { ColDef, GridOptions } from "ag-grid-community";
import { Flex, TabsProps, Typography } from "antd";
import i18next from "i18next";

import { CalculateOrderVariables } from "api/reactQuery/queryComponents";
import {
  BSCoreServicesDtoMarketplaceOrderDto,
  BSCoreServicesDtoMarketplaceOrderProductDto,
  BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse,
} from "api/reactQuery/querySchemas";
import {
  ProductLengthBadge,
  StyledAgGrid,
  StyledCardTabs,
  StyledTabs,
} from "components/MpOrder/CreateOrder/OrderProductsTab/styled";
import useColDefs from "components/MpOrder/CreateOrder/OrderProductsTab/useColDefs";
import { getGridDefaultProps } from "constants/agGridConstants";

const defaultColumnDefs: ColDef = {
  autoHeaderHeight: true,
  flex: 1,
  maxWidth: 500,
  minWidth: 80,
  resizable: true,
  sortable: false,
  tooltipValueGetter(params) {
    return params.valueFormatted ? params.valueFormatted : params.value;
  },
  wrapHeaderText: true,
};

type Props = {
  orderData?: BSCoreServicesDtoMarketplaceOrderDto;
  enableEditOrderedQuantities: boolean;
  showAllProductsAvailability: boolean;
  showOnInvoiceProductsAvailability: boolean;
  showMultipackProductsAvailability: boolean;
  showGasProductsAvailability: boolean;
  calculateOrder: UseMutateFunction<
    BSCoreServicesDtoMarketplaceOrderDto,
    { status: "unknown"; payload: string },
    CalculateOrderVariables,
    unknown
  >;
  selectedDepots: BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse[];
  isOrderSubmitted: boolean;
  hasCalledSaveOrder: boolean;
};
enum PRODUCT_TABS {
  ALL = "allProducts",
  MULTIPACK = "multiPackProducts",
  ONINVOICE = "onInvoiceProducts",
  GAS = "gasProducts",
}

const OrderProductsTabs: React.FC<Props> = ({
  orderData,
  calculateOrder,
  enableEditOrderedQuantities,
  showAllProductsAvailability,
  showOnInvoiceProductsAvailability,
  showMultipackProductsAvailability,
  showGasProductsAvailability,
  selectedDepots,
  isOrderSubmitted,
  hasCalledSaveOrder,
}) => {
  const [activeTab, setActiveTab] = useState<PRODUCT_TABS>(PRODUCT_TABS.ALL);
  const [activeDepotId, setActiveDepotId] = useState(selectedDepots ? selectedDepots[0]?.id : null);

  const getFilteredProducts = (
    data: BSCoreServicesDtoMarketplaceOrderDto,
    isInvoiceProduct: boolean
  ) => {
    return data?.products?.filter((product) =>
      product.isInvoiceProduct === isInvoiceProduct &&
      !product.isMultiPack &&
      product.productTypeId !== 1 &&
      product.productTypeId !== 2
    );
  };

  const depotIndex = orderData?.childOrders?.findIndex(
    (child) => child.depotId === activeDepotId
  );

  const allProducts = getFilteredProducts(
    orderData?.childOrders[depotIndex],
    false
  );

  const onInvoiceProducts = getFilteredProducts(
    orderData?.childOrders[depotIndex],
    true
  );

  const multiPackProducts = orderData?.childOrders[depotIndex].products?.filter(
    (product) => product.isMultiPack
  );

  const gasProducts = orderData?.childOrders[depotIndex].products?.filter(
    (product) => product.productTypeId === 1 || product.productTypeId === 2
  );

  // Sum orderedUnits for the same product variant across all depots.
  // Limits (OnInvoice/Multipack/Gas) apply per product across the whole order,
  // so a row should turn red when the cumulative ordered amount exceeds the
  // limit, even if neither depot individually exceeds it.
  const getTotalOrderedAcrossDepots = (product: BSCoreServicesDtoMarketplaceOrderProductDto) =>
    orderData?.childOrders?.reduce((total, childOrder) => {
      const match = childOrder.products?.find(
        (p) =>
          p.productId === product.productId &&
          p.isInvoiceProduct === product.isInvoiceProduct &&
          p.isMultiPack === product.isMultiPack
      );

      return total + (match?.orderedUnits ?? 0);
    }, 0) ?? 0;

  // Availability-driven overflow only counts once the order has actually
  // been submitted via "Напред" (saveOrder), not just after a
  // CalculateOrder echo. Per ID-41 QA: pre-Next coloring rule applies
  // *only* to products with hard limits (OnInvoice / Multipack / Gas) —
  // any other product (or a limit product whose ordered amount is below
  // the limit) should not flash red until the demand is saved.
  const productHasAvailabilityOverflow = (product: BSCoreServicesDtoMarketplaceOrderProductDto) =>
    isOrderSubmitted &&
    hasCalledSaveOrder &&
    product.id &&
    product.currentAvailableUnits !== null &&
    product.currentAvailableUnits !== undefined &&
    product.orderedUnits > product.currentAvailableUnits;

  const productHasLimitOverflow = (product: BSCoreServicesDtoMarketplaceOrderProductDto,) => {
    const totalOrdered = getTotalOrderedAcrossDepots(product);

    const isOnInvoiceOverflow =
      product.isInvoiceProduct &&
      product.onInvoiceQuantityLimit !== null &&
      product.onInvoiceQuantityLimit !== undefined &&
      totalOrdered > product.onInvoiceQuantityLimit;

    const isMultiPackOverflow =
      product.isMultiPack &&
      product.multiPackQuantityLimit !== null &&
      product.multiPackQuantityLimit !== undefined &&
      totalOrdered > product.multiPackQuantityLimit;

    const isGasOverflow =
      (product.productTypeId === 1 || product.productTypeId === 2) &&
      product.compressedGasProductQuantityLimit !== null &&
      product.compressedGasProductQuantityLimit !== undefined &&
      totalOrdered > product.compressedGasProductQuantityLimit;

    return isOnInvoiceOverflow || isMultiPackOverflow || isGasOverflow;
  };

  const productHasAnyOverflow = (product: BSCoreServicesDtoMarketplaceOrderProductDto) =>
    productHasAvailabilityOverflow(product) || productHasLimitOverflow(product);

  // A depot is only flagged if it actually contributes to the overflow.
  // If one depot orders 1500/1440 alone and the other depot orders 0 for the
  // same product, only the contributing depot should show the warning —
  // matches the spec: "...и напишеш каквото и да е количество".
  const productContributesToOverflow = (product: BSCoreServicesDtoMarketplaceOrderProductDto) =>
    product.orderedUnits > 0 && productHasAnyOverflow(product);

  const tabHasLimitOverflow = (products?: BSCoreServicesDtoMarketplaceOrderProductDto[]) => !!products?.some(productHasLimitOverflow);

  const depotHasOverflow = (depotIndex: number) => !!orderData?.childOrders?.[depotIndex]?.products?.some(productContributesToOverflow);

  useEffect(() => {
    if (allProducts?.length) setActiveTab(PRODUCT_TABS.ALL);
    else if (onInvoiceProducts?.length) setActiveTab(PRODUCT_TABS.ONINVOICE);
    else if (multiPackProducts?.length) setActiveTab(PRODUCT_TABS.MULTIPACK);
    else if (gasProducts?.length) setActiveTab(PRODUCT_TABS.GAS);
  }, [
    allProducts?.length,
    multiPackProducts?.length,
    onInvoiceProducts?.length,
    gasProducts?.length,
  ]);

  const { columnDefs: allProductsColumnDefs } = useColDefs({
    enableEditOrderedQuantities,
    showCurrentAvailableUnits: showAllProductsAvailability,
  });
  const { columnDefs: onInvoiceColumnDefs } = useColDefs({
    enableEditOrderedQuantities,
    hasLimitOverflow: tabHasLimitOverflow(onInvoiceProducts),
    showCurrentAvailableUnits: showOnInvoiceProductsAvailability,
    showOnInvoiceQuantityLimit: true,
  });
  const { columnDefs: multiPackColumnDefs } = useColDefs({
    enableEditOrderedQuantities,
    hasLimitOverflow: tabHasLimitOverflow(multiPackProducts),
    showCurrentAvailableUnits: showMultipackProductsAvailability,
    showMultiPackQuantityLimit: true,
  });
  const { columnDefs: gasColumnDefs } = useColDefs({
    enableEditOrderedQuantities,
    hasLimitOverflow: tabHasLimitOverflow(gasProducts),
    showCurrentAvailableUnits: showGasProductsAvailability,
    showCompressedGasProductQuantityLimit: true,
  });

  const columnDefsForTabs: { [key in PRODUCT_TABS]: ColDef<BSCoreServicesDtoMarketplaceOrderProductDto>[]; } = {
    [PRODUCT_TABS.ALL]: allProductsColumnDefs,
    [PRODUCT_TABS.MULTIPACK]: multiPackColumnDefs,
    [PRODUCT_TABS.ONINVOICE]: onInvoiceColumnDefs,
    [PRODUCT_TABS.GAS]: gasColumnDefs,
  };
  const productsForTabs: { [key in PRODUCT_TABS]: BSCoreServicesDtoMarketplaceOrderProductDto[]; } = {
    [PRODUCT_TABS.ALL]: allProducts,
    [PRODUCT_TABS.MULTIPACK]: multiPackProducts,
    [PRODUCT_TABS.ONINVOICE]: onInvoiceProducts,
    [PRODUCT_TABS.GAS]: gasProducts,
  };

  const onCellEditingStopped: GridOptions<BSCoreServicesDtoMarketplaceOrderProductDto>["onCellEditingStopped"] =
    (event) => {
      const tempData = { ...orderData };
      const product = tempData?.childOrders
        .find((depot) => depot.depotId === activeDepotId)
        ?.products?.find(
          (product) =>
            product.feTableId === event.data.feTableId &&
            product.isInvoiceProduct === event.data.isInvoiceProduct
        );

      if (product) {
        if (event.colDef.field === "orderedQuantitiesInUnits") {
          product.orderedQuantitiesInUnits = event.newValue === null ? 0 : event.newValue;
        }

        if (event.colDef.field === "orderedQuantitiesInPallets") {
          product.orderedQuantitiesInPallets = event.newValue === null ? 0 : event.newValue;
        }
      }

      event.valueChanged &&
        calculateOrder({
          body: tempData,
        });
    };

  const getRowStyle: GridOptions<BSCoreServicesDtoMarketplaceOrderProductDto>["getRowStyle"] = useCallback(({ data }) => {
    const totalOrdered = getTotalOrderedAcrossDepots(data);

    if (
      productHasAvailabilityOverflow(data) ||
      (activeTab === PRODUCT_TABS.ONINVOICE && data.orderedUnits > 0 && totalOrdered > data.onInvoiceQuantityLimit) ||
      (activeTab === PRODUCT_TABS.MULTIPACK && data.orderedUnits > 0 && totalOrdered > data.multiPackQuantityLimit) ||
      (activeTab === PRODUCT_TABS.GAS && data.orderedUnits > 0 && totalOrdered > data.compressedGasProductQuantityLimit)
    )
      return {
        background: "#FED9D9",
      };
  },
    [activeTab, isOrderSubmitted, orderData]
  );

  const renderGrid = (height: number) => (
    <div style={{ height }}>
      <StyledAgGrid
        key={String(isOrderSubmitted)}
        singleClickEdit
        rowHeight={64}
        getRowStyle={getRowStyle}
        getRowId={({ data }) => String(data.feTableId)}
        rowData={productsForTabs[activeTab].filter((product) => product.parentId === null)}
        columnDefs={columnDefsForTabs[activeTab]}
        onCellEditingStopped={onCellEditingStopped}
        suppressAnimationFrame={true}
        defaultColDef={defaultColumnDefs}
        {...getGridDefaultProps({ includeDefaultColDefs: false })}
        containerStyle={{ height: "100%", marginTop: 0 }}
      />
    </div>
  );

  const getProductBadges = (tab: PRODUCT_TABS) => {
    const products = productsForTabs[tab];

    const warningLabel = () => {
      if (products?.some(productHasAnyOverflow)) {
        return (
          <WarningOutlined style={{ color: "red", fontSize: 20, verticalAlign: "middle" }} />
        );
      }
    };

    return (
      <>
        <ProductLengthBadge>{products?.length}</ProductLengthBadge>
        {warningLabel()}
      </>
    );
  };

  const items: TabsProps["items"] = [
    !!allProducts?.length && {
      children: renderGrid(720),
      key: PRODUCT_TABS.ALL,
      label: (
        <>
          {i18next.t("all_products")}
          {getProductBadges(PRODUCT_TABS.ALL)}
        </>
      ),
    },
    !!onInvoiceProducts?.length && {
      children: renderGrid(400),
      key: PRODUCT_TABS.ONINVOICE,
      label: (
        <>
          {i18next.t("on_invoice_products")}
          {getProductBadges(PRODUCT_TABS.ONINVOICE)}
        </>
      ),
    },
    !!multiPackProducts?.length && {
      children: renderGrid(400),
      key: PRODUCT_TABS.MULTIPACK,
      label: (
        <>
          {i18next.t("multipack_products")}
          {getProductBadges(PRODUCT_TABS.MULTIPACK)}
        </>
      ),
    },
    !!gasProducts?.length && {
      children: renderGrid(400),
      key: PRODUCT_TABS.GAS,
      label: (
        <>
          {i18next.t("gas_products")}
          {getProductBadges(PRODUCT_TABS.GAS)}
        </>
      ),
    },
  ].filter(Boolean) as TabsProps["items"];

  const depotTabs: TabsProps["items"] = [
    {
      children: (
        <StyledTabs
          defaultActiveKey={activeTab}
          activeKey={activeTab}
          onChange={(key: PRODUCT_TABS) => setActiveTab(key)}
          items={items}
          className="productTabs"
        />
      ),
      key: String(selectedDepots[0]?.id),
      label: (
        <>
          <HomeOutlined
            style={{ color: "#7C7B7B", fontSize: 18, marginRight: "10px" }}
          />
          {selectedDepots[0]?.officialDepotName}
          {depotHasOverflow(0) && (
            <WarningOutlined
              style={{ color: "red", fontSize: 20, verticalAlign: "middle" }}
            />
          )}
        </>
      ),
    },
    selectedDepots.length > 1 && {
      children: (
        <StyledTabs
          defaultActiveKey={activeTab}
          activeKey={activeTab}
          onChange={(key: PRODUCT_TABS) => setActiveTab(key)}
          items={items}
          className="productTabs"
        />
      ),
      key: String(selectedDepots[1].id),
      label: (
        <>
          <HomeOutlined
            style={{ color: "#7C7B7B", fontSize: 18, marginRight: "10px" }}
          />
          {selectedDepots[1].officialDepotName}
          {depotHasOverflow(1) && (
            <WarningOutlined
              style={{ color: "red", fontSize: 20, verticalAlign: "middle" }}
            />
          )}
        </>
      ),
    },
  ].filter(Boolean) as TabsProps["items"];

  return !allProducts?.length &&
    !multiPackProducts?.length &&
    !onInvoiceProducts?.length &&
    !gasProducts?.length ? (
    <Flex justify="center">
      <Typography.Title style={{ color: "#6d6d6d", marginTop: 50 }}>
        {i18next.t("no_products")}
      </Typography.Title>
    </Flex>
  ) : (
    <StyledCardTabs
      onChange={(key) => setActiveDepotId(Number(key))}
      items={depotTabs}
      type="card"
      tabBarGutter={5}
    />
  );
};

export default OrderProductsTabs;
